import { Box, Typography } from '@mui/material';

import { LABEL_MIN_PX, segmentHeight, type ChartModel, type ChartView, type SegmentKind } from '../districtStatsChart';
import { DASH } from '../tokens';
import { ChartViewToggle } from './ChartViewToggle';

const FILL: Record<SegmentKind, string> = {
  dark: DASH.stats.barDark,
  pale: DASH.stats.barPale,
  grey: DASH.stats.barGrey,
};

const BODY_2 = { fontSize: 12, lineHeight: '16px', fontWeight: 500, color: DASH.muted } as const;

export function StudentsByGradeChart({
  model,
  view,
  onViewChange,
}: {
  model: ChartModel;
  view: ChartView;
  onViewChange: (view: ChartView) => void;
}) {
  return (
    <Box
      data-testid="students-by-grade-chart"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        padding: '16px 24px 20px',
        bgcolor: DASH.stats.tileBg,
        borderRadius: '8px',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', minHeight: 34 }}>
          <Typography component="b" sx={{ fontSize: 16, lineHeight: '24px', fontWeight: 700, color: DASH.muted }}>
            {model.title}
          </Typography>
          {model.toggle ? (
            <ChartViewToggle view={view} onChange={onViewChange} />
          ) : model.note ? (
            <Typography component="small" sx={BODY_2}>
              {model.note}
            </Typography>
          ) : null}
        </Box>
        {model.legend ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            {model.legend.map((entry) => (
              <Box key={entry.kind} sx={{ display: 'flex', alignItems: 'center', gap: '8px', ...BODY_2 }}>
                <Box component="i" sx={{ width: 12, height: 12, borderRadius: '4px', flexShrink: 0, bgcolor: FILL[entry.kind] }} />
                {entry.text}
              </Box>
            ))}
          </Box>
        ) : null}
      </Box>
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '12px', height: 176 }}>
          {model.columns.map((column) => (
            <Box
              key={column.grade}
              data-testid="grade-column"
              sx={{
                flex: '1 1 0',
                minWidth: 0,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              {model.totals === false ? null : (
                <Typography data-testid="grade-total" component="span" sx={{ fontSize: 12, lineHeight: '16px', fontWeight: 700, color: DASH.ink }}>
                  {column.total}
                </Typography>
              )}
              <Box sx={{ alignSelf: 'stretch', display: 'flex', flexDirection: 'column' }}>
                {column.segments.map((segment, index) => {
                  const height = segmentHeight(segment.count, model.max);
                  return (
                    <Box
                      key={segment.kind}
                      data-testid="bar-segment"
                      style={{ height: `${height}px` }}
                      sx={{
                        flexShrink: 0,
                        display: 'flex',
                        justifyContent: 'center',
                        paddingTop: '4px',
                        overflow: 'hidden',
                        boxSizing: 'border-box',
                        color: DASH.stats.barLabel,
                        fontSize: 12,
                        lineHeight: '16px',
                        fontWeight: 600,
                        bgcolor: FILL[segment.kind],
                        borderRadius: index === 0 ? '4px 4px 0 0' : 0,
                      }}
                    >
                      {segment.labeled && height >= LABEL_MIN_PX ? segment.count : ''}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          ))}
        </Box>
        <Box data-testid="grade-axis" sx={{ display: 'flex', gap: '12px', paddingTop: '8px', borderTop: `1px solid ${DASH.rowBorder}` }}>
          {model.columns.map((column) => (
            <Typography key={column.grade} component="span" sx={{ flex: '1 1 0', minWidth: 0, textAlign: 'center', ...BODY_2 }}>
              {column.grade}
            </Typography>
          ))}
        </Box>
      </Box>
    </Box>
  );
}
